import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import StatusBadge from "../../../../components/common/StatusBadge";
import QuotationCreateModal from "./QuotationCreateModal";
import { QUOTATION_STATUS_CLASS, QUOTATION_STATUS_LABEL } from "../quotationStatusMaps";
import { useListQuotationsQuery } from "../quotationsApi";
import { useCanCreateQuotation } from "../useQuotationPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const money = (n) => Number(n || 0).toFixed(2);

const QuotationCompactList = () => {
  const canCreate = useCanCreateQuotation();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListQuotationsQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    search: search || undefined,
    pageSize: 200,
  });

  const items = data?.items || [];

  return (
    <>
    <MasterDetailListShell
      title="Quotations"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => setShowCreateModal(true)}>
            <PlusCircle className="me-2" size={16} />
            New Quotation
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search customer or quotation number"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No quotations match these filters."
      items={items}
      getKey={(q) => q.recordGuid}
      resetKey={`${search}|${status}|${selectedBranchId}`}
      renderItem={(q) => {
        const isSelected = String(q.recordGuid) === String(selectedRecordGuid);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.quotationDetail(q.recordGuid))}
            avatarLabel={(q.customerName || "?").trim().slice(0, 2).toUpperCase()}
            title={
              // Customer leads, quotation number is the detail line (2026-09-12).
              <>
                {q.customerName || q.quotationNumber} <span className="text-muted ms-1">{money(q.totalAmount)}</span>
              </>
            }
            subtitle={<>{q.quotationNumber} &middot; {q.branchCode}</>}
            badge={
              <span className="me-2">
                <StatusBadge status={q.status} classMap={QUOTATION_STATUS_CLASS} labelMap={QUOTATION_STATUS_LABEL} />
              </span>
            }
            actions={
              <button
                type="button"
                className="btn btn-link p-1"
                title="View"
                onClick={(ev) => {
                  ev.stopPropagation();
                  navigate(paths.quotationDetail(q.recordGuid));
                }}
              >
                <Eye size={15} />
              </button>
            }
          />
        );
      }}
    />
    <QuotationCreateModal show={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </>
  );
};

export default QuotationCompactList;
